/**
 * Importable Product-event mapping contract for Configurable Experiences.
 *
 * This module is the schema later mapping, emit, and authoring work
 * must import. It does **not** resolve paths, emit events, or turn
 * Product tracking on/off. Extract and apply carry dual-shape
 * `productFields` as `Record<string, ProductFieldMapping>`.
 *
 * Shared dataPath/literal classification lives in `fieldSource.ts`.
 * This module adds Product wire-key rules on top of that parser.
 *
 * Config lives on the existing tree — never a parallel `productEvents` blob
 * (rejected SDD §7 illustration):
 *   - field mapping: `data.root.props.tracking.productFields`
 *   - additive dimensions: `data.root.props.tracking.customDimensions`
 *   - per-placement on/off: `_meta.tracking.enabled` on the DSL instance
 *
 * Wire keys match `createEventingPayloadsDE` Product payload fields
 * (`name`, `productId`, `sku`, …), not Data Binding glossary aliases
 * (`productName`, `offerId`).
 *
 * Human-readable copy:
 * `packages/dynamic-experiences/docs/product-event-mapping-contract.md`
 *
 * Public import (Admin / authoring):
 * `@highereducation/dynamic-experience/utils`
 */

import {
  parseFieldSource,
  type CustomDimensionMapping,
  type FieldSource,
  type FieldSourceDataPath,
  type FieldSourceLiteral,
  type FieldSourceParseResult,
} from './fieldSource';

export {
  parseCustomDimensionMapping,
  parseFieldSource,
  type CustomDimensionMapping,
  type CustomDimensionParseResult,
  type FieldSource,
  type FieldSourceDataPath,
  type FieldSourceLiteral,
  type FieldSourceParseResult,
} from './fieldSource';

/**
 * Allowlisted Product wire keys authors may map.
 */
export const PRODUCT_EVENT_WIRE_KEYS = [
  'variant',
  'category',
  'name',
  'formatType',
  'formatSubtype',
  'brand',
  'productId',
  'sku',
] as const;

/**
 * Product wire field name authors may map onto.
 */
export type ProductEventWireKey = (typeof PRODUCT_EVENT_WIRE_KEYS)[number];

/**
 * Product payload keys that exist on the wire but are **not** mapping
 * targets (`positionEngine` stays builder-owned until that producer
 * change is live).
 */
export const PRODUCT_EVENT_NON_MAPPABLE_WIRE_KEYS = [
  'location',
  'position',
  'positionEngine',
  'listId',
] as const;

/**
 * `productFields` keys that may use `{ literal }`. `customDimensions`
 * entries also allow literals, but they are a sibling array — not a
 * key inside `productFields`.
 */
export const PRODUCT_EVENT_LITERAL_WIRE_KEYS = [
  'formatType',
  'formatSubtype',
] as const;

/**
 * Wire keys whose **emitted** override must be slugified (lowercase,
 * trim, strip non-word, collapse whitespace to `-`) using the same helper
 * as `createEventingPayloadsDE`.
 *
 * Payload builders also slugify `variant` / `category` / `name` at emit.
 * The resolver may slugify configured values as well; the emit-time pass
 * is treated as idempotent.
 */
export const PRODUCT_EVENT_SLUGIFY_WIRE_KEYS = [
  'variant',
  'category',
  'name',
  'formatType',
  'formatSubtype',
] as const;

/**
 * Identity fields. Never slugify — `brand` is already a school slug on
 * the wire, and `productId` / `sku` are identifiers.
 */
export const PRODUCT_EVENT_NEVER_SLUGIFY_WIRE_KEYS = [
  'productId',
  'sku',
  'brand',
] as const;

/**
 * Where Product mapping config lives on a DSL document / instance.
 * There is no `tracking.productEvents` tree.
 */
export const PRODUCT_EVENT_CONFIG_LOCATION = {
  productFields: 'data.root.props.tracking.productFields',
  customDimensions: 'data.root.props.tracking.customDimensions',
  instanceEnabled: '_meta.tracking.enabled',
} as const;

/**
 * Resolve root for the same `productFields` map.
 *
 * Entry Loaded / Viewed / Clicked use **live** `flowContext` (from
 * `buildFlowContext`), not mount-only host `dcs`. EL / result cards
 * use the repeater `item` (Offerings search/match row). Authors may
 * point any allowlisted wire field at any key on that root, including
 * `_meta.leadFieldAssociated` / `customField` handles.
 */
export const PRODUCT_EVENT_RESOLVE_ROOT = {
  entry: 'flowContext',
  editorialListing: 'item',
} as const;

/**
 * Recommended EL override only. Do not flip the platform default
 * (`programId`); authors may set `productId: { dataPath: "offeringUuid" }`.
 */
export const RECOMMENDED_EL_PRODUCT_ID_DATAPATH = 'offeringUuid';

/**
 * What today's builders emit when a wire key is unset. Tokens are the
 * source of the default, not resolved values.
 */
export const PRODUCT_EVENT_UNSET_DEFAULTS = {
  variant: {
    entry: 'slugify(dcs.degree / presets.degree)',
    editorialListing: 'slugify(degreeSlug via presets)',
  },
  category: {
    entry: 'slugify(dcs.category / presets)',
    editorialListing: 'slugify(categorySlug)',
  },
  name: {
    entry: 'slugify(dcs.subject / presets)',
    editorialListing: 'slugify(subjectSlug)',
  },
  formatType: {
    entry: 'getTypeInfo / Eventing Updates literals',
    editorialListing: 'dexp-offering / registry',
  },
  formatSubtype: {
    entry: 'Eventing Updates / step',
    editorialListing: 'dexp-results / dexp-unqualified / registry',
  },
  brand: {
    entry: 'omitted',
    editorialListing: 'schoolSlug',
  },
  productId: {
    entry: 'omitted',
    editorialListing: 'programId',
  },
  sku: {
    entry: 'omitted',
    editorialListing: 'capId',
  },
} as const satisfies Record<
  ProductEventWireKey,
  { readonly entry: string; readonly editorialListing: string }
>;

/**
 * Legacy string path, equivalent to `{ dataPath: value }`.
 */
export type ProductFieldLegacyPathMapping = string;

/**
 * Path mapping against the resolve root (`flowContext` or repeater `item`).
 */
export type ProductFieldDataPathMapping = FieldSourceDataPath;

/**
 * Literal mapping. Only valid on {@link PRODUCT_EVENT_LITERAL_WIRE_KEYS}.
 */
export type ProductFieldLiteralMapping = FieldSourceLiteral;

/**
 * One `productFields` value. `{ dataPath, literal }` together is
 * rejected at parse time (authoring mistake).
 */
export type ProductFieldMapping = FieldSource;

/**
 * Dual-shape `productFields` map keyed by allowlisted wire names.
 */
export type ProductFieldsConfig = Partial<
  Record<ProductEventWireKey, ProductFieldMapping>
>;

/**
 * Root tracking block for Product mapping. `productSource` stays on
 * root `tracking` (already ResultPage-owned) and is not restated here.
 */
export interface ProductEventTrackingConfig {
  readonly productFields?: ProductFieldsConfig;
  readonly customDimensions?: readonly CustomDimensionMapping[];
}

/**
 * Classification of one `productFields` entry. Does not interpolate.
 */
export type ProductFieldParseResult =
  | FieldSourceParseResult
  | {
      readonly kind: 'invalid';
      readonly reason:
        | 'both-dataPath-and-literal'
        | 'literal-not-allowed'
        | 'unknown-key'
        | 'malformed';
    };

const PRODUCT_EVENT_WIRE_KEY_SET: ReadonlySet<string> = new Set(
  PRODUCT_EVENT_WIRE_KEYS,
);

const PRODUCT_EVENT_LITERAL_WIRE_KEY_SET: ReadonlySet<string> = new Set(
  PRODUCT_EVENT_LITERAL_WIRE_KEYS,
);

const PRODUCT_EVENT_SLUGIFY_WIRE_KEY_SET: ReadonlySet<string> = new Set(
  PRODUCT_EVENT_SLUGIFY_WIRE_KEYS,
);

/**
 * Type-guard for {@link PRODUCT_EVENT_WIRE_KEYS}.
 *
 * @param {string} key - Candidate productFields key
 * @returns {boolean} True when `key` is an allowlisted mapping target
 */
export function isProductEventWireKey(key: string): key is ProductEventWireKey {
  return PRODUCT_EVENT_WIRE_KEY_SET.has(key);
}

/**
 * Whether `{ literal }` is valid on this `productFields` key.
 *
 * @param {string} key - Candidate productFields key
 * @returns {boolean} True only for `formatType` and `formatSubtype`
 */
export function allowsProductFieldLiteral(key: string): boolean {
  return PRODUCT_EVENT_LITERAL_WIRE_KEY_SET.has(key);
}

/**
 * Whether the **emitted** value for this wire key must be a slug.
 *
 * Does not choose the slugify call site. Canonical triad is already
 * slugified in payload builders — do not slugify those keys twice.
 *
 * @param {string} key - Product wire field name
 * @returns {boolean} True for variant/category/name/formatType/formatSubtype
 */
export function shouldSlugifyProductField(key: string): boolean {
  return PRODUCT_EVENT_SLUGIFY_WIRE_KEY_SET.has(key);
}

/**
 * Whether customDimensions values are slugified on emit.
 *
 * @returns {boolean} Always true
 */
export function shouldSlugifyCustomDimensionValue(): boolean {
  return true;
}

/**
 * Classify one `productFields` value without resolving the path.
 *
 * Legacy non-empty strings are `{ dataPath }` even for unknown keys
 * (those maps still spread). Dual-shape objects on unknown keys are
 * `unknown-key`. `{ dataPath, literal }` on the same key is invalid.
 *
 * @param {string} wireKey - Product wire field the author mapped
 * @param {unknown} value - Raw config value
 * @returns {ProductFieldParseResult} Parse classification
 */
export function parseProductFieldMapping(
  wireKey: string,
  value: unknown,
): ProductFieldParseResult {
  const parsed = parseFieldSource(value);
  if (
    parsed.kind === 'empty' ||
    parsed.kind === 'invalid' ||
    typeof value === 'string'
  ) {
    return parsed;
  }
  if (!isProductEventWireKey(wireKey)) {
    return { kind: 'invalid', reason: 'unknown-key' };
  }
  if (parsed.kind === 'literal' && !allowsProductFieldLiteral(wireKey)) {
    return { kind: 'invalid', reason: 'literal-not-allowed' };
  }
  return parsed;
}
